import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { Button, Filter, FilterPill, InputField, TableTitle } from '../../../components/common'
import { useDebounce } from '../../../components/CustomHooks/useCustomHooks'
import { LanguageContext } from '../../../context/LanguageContext'
import { util } from '../../../util'

/** This component is responsible to display search input, filter button, Filter menu and filter pills.
 * @returns {JSX} -> return a Table Filter Tool bar with search input, filter button and filter pills
 */
const TableFilterToolBar = ({
    title,
    size,
    numberOfRows,
    searchPlaceHolder,
    isFromProduct,
    dispatchClearAllFilters,
    renderFilterComp,
    applied_filters,
    isSearch,
    searchKey,
    dispatchFilters,
    debounceSearch = false,
    searchInputValidator,
    searchErrorMessage = '',
    minLengthErrorMessage = '',
    renderDefaultFields,
    renderExtraTools,
    renderTitleFilters,
    onFilterPillClear,
    isFilter = true,
    isTitle = true,
}) => {
    const { rtl, strings } = useContext(LanguageContext)

    const searchFilter = applied_filters.find(
        filter => filter && filter.placed_at && filter.placed_at === 'tableToolBar'
    )
    const [search, setSearch] = useState(!!searchFilter ? searchFilter.display_value : '')
    const [searchError, setSearchError] = useState('')
    const debounceSearchValue = useDebounce(search, 500, debounceSearch)

    useEffect(() => {
        /** When search filter was cleared by clicking clearAll button then
         * clear search input value from search input field  */
        if (searchFilter) {
            search !== searchFilter.display_value && setSearch(searchFilter.display_value)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applied_filters])

    useEffect(() => {
        if (
            debounceSearch &&
            (debounceSearchValue.length || (searchFilter && searchFilter.post_value)) &&
            search !== searchFilter.post_value &&
            !searchError
        ) {
            updateSearchFilter(debounceSearchValue)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounceSearchValue])

    const updateSearchFilter = value => {
        const filterState = [...applied_filters]
        const updatedFilterState = filterState.map(filter => {
            let updatedFilter = { ...filter }
            if (
                (searchKey && filter.key === searchKey) ||
                filter.key === 'name' ||
                filter.key === 'title' ||
                filter.key === 'store_id' ||
                filter.key === 'search'
            ) {
                let postValue = value
                if (!!filter.postUtil) {
                    postValue = filter.postUtil({ value })
                }
                const payload = { display_value: value, post_value: postValue }
                updatedFilter = util.shallowHelper(filter, payload)
            }
            return updatedFilter
        })
        dispatchFilters(updatedFilterState)
    }

    const searchMemo = useMemo(
        () => ({
            end: util.SearchIcon,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    const clearPillHandler = useCallback(
        keyPill => {
            const filterState = [...applied_filters]
            //Clear the state of the pill and dispatch the updated state to redux
            const clearPillState = filterState.map(filter => {
                const updatedFilterState = { ...filter }
                if (updatedFilterState.key === keyPill) {
                    updatedFilterState['display_value'] = updatedFilterState.defaultDisplayValue
                    updatedFilterState['post_value'] = updatedFilterState.defaultPostValue
                    if (updatedFilterState.type === 'range') {
                        updatedFilterState['start_value'] = ''
                        updatedFilterState['end_value'] = ''
                        updatedFilterState['display_value'] = updatedFilterState.defaultDisplayValue
                    } else if (updatedFilterState.type === 'option') {
                        updatedFilterState['display_value'] = ''
                        updatedFilterState['post_value'] = ''
                    } else {
                        updatedFilterState['display_value'] = updatedFilterState.defaultDisplayValue
                        updatedFilterState['post_value'] = updatedFilterState.defaultPostValue
                    }
                }
                return updatedFilterState
            })

            const transformedFilters = !!onFilterPillClear
                ? onFilterPillClear(clearPillState)
                : clearPillState
            dispatchFilters(transformedFilters)
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [applied_filters]
    )

    const searchInputChangeHandler = useCallback(e => {
        const searchInput = e.target.value
        if (!searchInput) {
            setSearch('')
            setSearchError('')
        }

        if (!!searchInputValidator && searchInputValidator(searchInput)) {
            setSearchError('')
        } else if (searchInput.trim().length) {
            if (searchInput.trim().length < 3 && isFromProduct) {
                setSearchError(minLengthErrorMessage)
            } else setSearchError(searchErrorMessage)
        }

        setSearch(searchInput)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const enterSearchHandler = useCallback(
        e => {
            if (debounceSearch) {
                return
            }
            const enteredVal = e.target.value
            if (e.keyCode === 13 && (enteredVal !== '' || searchFilter.post_value.length)) {
                updateSearchFilter(enteredVal)
                setSearch('')
            }
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [applied_filters, searchFilter]
    )

    const renderFilters = () => (
        <div className={'table-toolbar__search'}>
            {isSearch && (
                <InputField
                    className={util.joinClasses('search', isSearch && `${rtl ? 'ml-4' : 'mr-4'}`)}
                    size={size || 'sm'}
                    value={search}
                    name={'search'}
                    onChange={searchInputChangeHandler}
                    onKeyUp={enterSearchHandler}
                    inputProps={searchMemo}
                    label={strings.searchLabel}
                    error={searchError}
                    placeholder={searchPlaceHolder}
                />
            )}
            {isFilter && <Filter renderFilterComp={renderFilterComp} />}
            {!!renderExtraTools && renderExtraTools()}
        </div>
    )

    const renderFilterPills = () =>
        applied_filters &&
        applied_filters.filter(
            ({ display_value, hideFilterPillUtil = undefined }) =>
                display_value &&
                display_value.length > 0 &&
                (!!hideFilterPillUtil ? !hideFilterPillUtil(display_value) : true)
        ).length > 0 && (
            <div className={'table-toolbar__pill-list'}>
                {applied_filters.map(
                    (
                        {
                            key,
                            display_name,
                            type,
                            display_value,
                            hideFilterPillUtil = undefined,
                            displayPillUtil,
                            hideFilterPill = false,
                        },
                        i
                    ) => {
                        hideFilterPill = !!hideFilterPillUtil
                            ? hideFilterPillUtil(display_value)
                            : hideFilterPill

                        display_value = !!displayPillUtil
                            ? displayPillUtil(display_value)
                            : display_value
                        return (
                            <FilterPill
                                className={rtl ? 'ml-1' : 'mr-1'}
                                key={i}
                                label={display_name}
                                keyPill={key}
                                clearPillHandler={clearPillHandler}
                                filterList={display_value}
                                type={type}
                                hideFilterPill={hideFilterPill}
                            />
                        )
                    }
                )}
                <Button
                    variant={'basic'}
                    color={'primary'}
                    margin={'mt-3'}
                    onClick={dispatchClearAllFilters}
                >
                    {strings.CLEAR_ALL}
                </Button>
            </div>
        )

    const renderTableCount = () =>
        isFilter ? (
            <p className={'paragraph sub regular mt-6'}>
                {`${numberOfRows} ${strings.tableItems}`}
            </p>
        ) : null

    return (
        <div className={'table-toolbar'}>
            {isTitle && (
                <div className={'table-toolbar__title'}>
                    <TableTitle title={title} />
                    {!!renderTitleFilters && renderTitleFilters()}
                </div>
            )}
            <div className={'flex-row space-between'}>
                {!!renderDefaultFields && renderDefaultFields()}
                {renderFilters()}
            </div>
            {renderFilterPills()}
            {renderTableCount()}
        </div>
    )
}

TableFilterToolBar.propTypes = {
    title: PropTypes.string,
    numberOfRows: PropTypes.number,
    searchPlaceHolder: PropTypes.string,
    dispatchClearAllFilters: PropTypes.func,
    renderFilterComp: PropTypes.func,
    applied_filters: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    isSearch: PropTypes.bool,
    searchKey: PropTypes.string,
    dispatchFilters: PropTypes.func,
    debounceSearch: PropTypes.bool,
    searchInputValidator: PropTypes.func,
}

const shouldComponentUpdate = (prevProps, nextProps) =>
    !(
        prevProps.numberOfRows !== nextProps.numberOfRows ||
        prevProps.applied_filters !== nextProps.applied_filters ||
        prevProps.title !== nextProps.title
    )

export default memo(TableFilterToolBar, shouldComponentUpdate)
